import { CSSObjectWithLabel } from 'react-select';

import { theme } from '../../common/ui/theme';

import { getSelectControlStyles } from './helpers/getSelectControlStyles';

interface SelectStylesOptions {
    positive?: boolean;
    negative?: boolean;
}

export const createSelectStyles = (options: SelectStylesOptions = {}) => ({
    option: (provided: CSSObjectWithLabel, state: any): CSSObjectWithLabel => {
        const optionStyles = {
            ...provided,
            padding: 0,
            margin: 0,
            borderRadius: theme.radii[4],
            backgroundColor: theme.colors.background.default,
            cursor: 'pointer',
        };

        if (state.isFocused) {
            return {
                ...optionStyles,
                backgroundColor: theme.colors.accent.background,
                ':active': {
                    backgroundColor: theme.colors.accent.background,
                },
            };
        }

        if (state.isSelected) {
            return {
                ...optionStyles,
                ':active': {
                    backgroundColor: theme.colors.accent.background,
                },
            };
        }

        return {
            ...optionStyles,
            ':active': {
                backgroundColor: theme.colors.accent.background,
            },
        };
    },

    control: (provided: CSSObjectWithLabel, state: any): CSSObjectWithLabel => {
        const { backgroundColor, boxShadow, hoverBoxShadow } = getSelectControlStyles(state, options);

        return {
            ...provided,
            backgroundColor,
            border: 'none',
            borderRadius: '6px',
            outline: 'none',
            boxShadow,
            minHeight: '32px',
            padding: `${theme.space[4]} ${theme.space[8]}`,
            transition: 'box-shadow 75ms',
            cursor: 'pointer',
            '&:hover': {
                border: 'none',
                boxShadow: hoverBoxShadow,
            },
        };
    },

    menu: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        boxShadow: 'none',
        zIndex: theme.zIndex.interactive,
        marginTop: theme.space[6],
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
    }),

    menuList: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        backgroundColor: theme.colors.background.default,
        borderRadius: theme.radii[4],
        padding: theme.space[4],
        boxShadow: theme.shadows.large,
    }),

    placeholder: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        fontSize: theme.fontSizes[14],
        lineHeight: '24px',
        color: theme.colors.foreground.subtle,
        fontWeight: theme.fontWeights[400],
        margin: 0,
        padding: 0,
    }),

    singleValue: (provided: CSSObjectWithLabel, state: any): CSSObjectWithLabel => ({
        ...provided,
        fontSize: theme.fontSizes[14],
        lineHeight: '24px',
        color: state.isDisabled ? theme.colors.foreground.disabled : theme.colors.foreground.default,
        fontWeight: theme.fontWeights[400],
        paddingLeft: 0,
        marginLeft: 0,
    }),

    valueContainer: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        flexWrap: 'nowrap',
        padding: 0,
    }),

    input: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        paddingLeft: 0,
        paddingRight: 0,
        margin: 0,
    }),

    indicatorsContainer: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        padding: 0,
    }),

    dropdownIndicator: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...provided,
        padding: 0,
    }),
});
